import { useState } from 'react';
import { useReadContracts } from 'wagmi';
import { bondDepositoryAbi, referralManagerAbi, stakingManagerAbi } from '@syntra/abi';
import { BadgeTitle, Button, Card, SectionHead } from '../components/ui/primitives.tsx';
import { DEPLOYMENT as D, EXPLORER } from '../config/env.ts';
import { NEWS } from '../content/news.ts';
import { UpcomingChanges } from '../components/governance/UpcomingChanges.tsx';
import { useI18n } from '../i18n/index.tsx';
import { fmtBps, fmtUsd, shortAddr } from '../lib/format.ts';
import { RANK_KEYS } from './Referral.tsx';
import { PERMANENT, useProtocol } from '../hooks/useProtocol.ts';
import { fmtSig } from '../lib/format.ts';

/** Every number on this page is read from the contracts, so the docs cannot drift from the live parameters. */
export function Docs() {
  const { t } = useI18n();
  const { data: protocol } = useProtocol();
  const live = useReadContracts({
    allowFailure: false,
    contracts: [
      { address: D.bonds, abi: bondDepositoryAbi, functionName: 'FEE_BPS' },
      { address: D.staking, abi: stakingManagerAbi, functionName: 'fees' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'directBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'capMultiplierBps' },
      { address: D.referral, abi: referralManagerAbi, functionName: 'minSideBps' },
    ],
  });
  const ranks = useReadContracts({
    allowFailure: false,
    contracts: [1, 2, 3, 4, 5].map((r) => ({ address: D.referral, abi: referralManagerAbi, functionName: 'rankConfig', args: [r] }) as const),
  });
  const [bondFee, fees, direct, cap, minSide] = live.data ?? [];
  const show = (v: bigint | number | undefined) => (v === undefined ? '—' : fmtBps(v));
  // per plan: [no lock (the SYN ↔ sSYN conversion), 30 / 90 / 180 / 365-day locks, permanent]
  const byPlan = (i: 0 | 1) =>
    fees
      ? t('docs.feePlans', { noLock: show(fees[i][0]), locked: show(fees[i][1]), perm: fees[i][PERMANENT] === 0n ? t('stake.noFee') : show(fees[i][PERMANENT]) })
      : '—';
  const contracts: [string, `0x${string}`][] = [
    ['SYN', D.syntra],
    ['sSYN', D.sSyntra],
    ['Staking', D.staking],
    ['Bonds', D.bonds],
    ['Referral', D.referral],
    ['Treasury', D.treasury],
    ['Buyback', D.buyback],
    ['Price oracle', D.oracle],
    ['Minter registry', D.registry],
    ['Timelock', D.timelock],
    ['SYN-USDT pool', D.syntraUsdtPair],
    ...(D.lpLock ? ([['Liquidity lock (12 months)', D.lpLock]] as [string, `0x${string}`][]) : []),
  ];
  return (
    <>
      <SectionHead eyebrow={t('docs.eyebrow')} title={t('docs.title')} />
      <UpcomingChanges />
      <Card title={t('news.title')}>
        <ul className="news soft" data-testid="news">
          {NEWS.map((n) => (
            <li key={n.key}>
              <span className="num news-date">{n.date}</span>
              <span>{t(n.key)}</span>
            </li>
          ))}
        </ul>
      </Card>
      <div className="grid two">
        <Card className="badge-card" title={<BadgeTitle icon="bond">{t('docs.bondH')}</BadgeTitle>}>
          <p className="soft">{t('docs.bondP')}</p>
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="stake">{t('docs.stakeH')}</BadgeTitle>}>
          <p className="soft">{t('docs.stakeP')}</p>
          <p className="soft" data-testid="docs-budget">
            {t('docs.budgetP', { cap: fmtSig(protocol.budget?.cap), wanted: fmtSig(protocol.budget?.wanted) })}
          </p>
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="referral">{t('docs.refH')}</BadgeTitle>}>
          <p className="soft">{t('docs.refP', { direct: show(direct), cap: show(cap) })}</p>
          <p className="soft">{t('docs.refCode')}</p>
        </Card>
        <Card className="badge-card" title={<BadgeTitle icon="liquidity">{t('docs.treasuryH')}</BadgeTitle>}>
          <p className="soft">{t('docs.treasuryP')}</p>
        </Card>
      </div>
      <div className="grid two">
        <Card title={t('docs.feesH')}>
          <table className="table" data-testid="fees">
            <thead>
              <tr>
                <th>{t('docs.action')}</th>
                <th>{t('docs.feeCol')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('docs.feeBond')}</td>
                <td>{show(bondFee)}</td>
              </tr>
              <tr>
                <td>{t('docs.feeStake')}</td>
                <td>{byPlan(0)}</td>
              </tr>
              <tr>
                <td>{t('docs.feeUnstake')}</td>
                <td>{byPlan(1)}</td>
              </tr>
              <tr>
                <td>{t('docs.feeSwap')}</td>
                <td>{t('docs.feeSwapV')}</td>
              </tr>
            </tbody>
          </table>
        </Card>
        <Card title={t('docs.ranksH')}>
          <table className="table" data-testid="ranks">
            <thead>
              <tr>
                <th>{t('docs.rankCol')}</th>
                <th>{t('docs.threshold')}</th>
                <th>{t('docs.perSide')}</th>
                <th>{t('docs.rateCol')}</th>
              </tr>
            </thead>
            <tbody>
              {(ranks.data ?? []).map((r, i) => (
                <tr key={i}>
                  <td>{t(RANK_KEYS[i + 1])}</td>
                  <td>{fmtUsd(r.thresholdUsd)}</td>
                  <td>{minSide !== undefined ? fmtUsd((r.thresholdUsd * BigInt(minSide)) / 10_000n) : '—'}</td>
                  <td>{fmtBps(r.rateBps)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <Card title={t('docs.risksH')}>
        <ul className="soft" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(['docs.risk1', 'docs.risk2', 'docs.risk3', 'docs.risk4', 'docs.risk5'] as const).map((k) => (
            <li key={k}>{t(k)}</li>
          ))}
        </ul>
      </Card>
      <Card title={t('docs.contractsH')}>
        <table className="table stack" data-testid="contracts">
          <thead>
            <tr>
              <th>{t('docs.contract')}</th>
              <th>{t('docs.address')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {contracts.map(([name, a]) => (
              <tr key={name}>
                <td data-label={t('docs.contract')}>{name}</td>
                <td data-label={t('docs.address')} className="num" title={a}>
                  {EXPLORER ? (
                    <a href={`${EXPLORER}/address/${a}`} target="_blank" rel="noreferrer">
                      {shortAddr(a)}
                    </a>
                  ) : (
                    shortAddr(a)
                  )}
                </td>
                <td>
                  <CopyButton value={a} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function CopyButton({ value }: { value: string }) {
  const { t } = useI18n();
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={async () => {
        await navigator.clipboard?.writeText(value).catch(() => undefined);
        setDone(true);
        setTimeout(() => setDone(false), 1200);
      }}
    >
      {done ? t('wallet.copied') : t('wallet.copy')}
    </Button>
  );
}
